import { Rocket } from "lucide-react";
import { usePermission } from "@ncp/api-client";
import { useLocale, useTranslation } from "@ncp/i18n";
import { Button, notifySaved, toast } from "@ncp/ui";
import { useEnvironments, usePromotionActions, usePromotions } from "./api";

/** Promote a published release to the next environment on the path; hidden in the last environment or once already sent. */
export function PromoteButton({ changeSetId }: { changeSetId: string }) {
  const { t } = useTranslation();
  const { locale } = useLocale();
  const { can } = usePermission();
  const allowed = can("platform.environments.promote");
  const envs = useEnvironments(allowed);
  const history = usePromotions(allowed);
  const { promote } = usePromotionActions();
  const next = envs.data?.environments.find((e) => e.key === envs.data?.next);
  if (!allowed || !next) return null;
  const nextName = locale === "ar" && next.nameAr ? next.nameAr : next.name;
  const sent = (history.data ?? []).some((p) => p.direction === "Outbound" && p.changeSetId === changeSetId && p.status !== "Failed");
  if (sent) return null;
  const run = async () => {
    try {
      const p = await promote.mutateAsync(changeSetId);
      if (p.status === "Failed") toast.error(t("admin:environments.promoteFailed", { error: p.error ?? "" }));
      else notifySaved(p, t("admin:environments.promoted", { env: nextName }));
    } catch { /* useSave reported the failure with its cause */ }
  };
  return (
    <Button variant="gold" title={t("admin:environments.promoteFromReleaseHint", { env: nextName })} loading={promote.isPending} onClick={() => void run()} data-testid="promote-release">
      <Rocket /> {t("admin:environments.promoteFromRelease", { env: nextName })}
    </Button>
  );
}
